import {
  createContext,
  type PropsWithChildren,
  useContext,
  useRef,
} from 'react';
import { useDispatch } from '@witmetrics/data-store';
import {
  type ApiErrorCallback,
  initiateDispatchSockets,
  type Join,
  type Leave,
  type Send,
  type SocketEventCallback,
  type Subscribe,
  type Unsubscribe,
} from '@witmetrics/unison-core';
import type { Socket } from '@/types/sockets';
import { useMountEffect } from '@/hooks/useMountEffect';
import { ACTIVE_WARNING } from './constants';
import { connectSocket } from './utils';

export type { SocketEventCallback, Subscribe, Unsubscribe, Join, Leave };

export type SocketsValue = {
  setApiErrorCallback: (cb: ApiErrorCallback) => void;
  subscribe: Subscribe;
  unsubscribe: Unsubscribe;
  join: Join;
  leave: Leave;
  send: Send;
  getSocket: () => Socket | null;
};

const SocketsContext = createContext<SocketsValue | undefined>(undefined);

export default function SocketsProvider({ children }: PropsWithChildren) {
  const apiErrorCallback = useRef<() => any>();
  const socket = useRef<Socket | null>(null);
  const dispatch = useDispatch();

  useMountEffect(() => {
    handleInitiateSocket();
  });

  const handleInitiateSocket = () => {
    if (socket.current !== null) socket.current.socket.disconnect();
    socket.current = connectSocket(handleApiError);
    initiateDispatchSockets(dispatch, socket.current.subscribe);
  };

  const handleSetApiErrorCallback = (callback: () => any) => {
    apiErrorCallback.current = callback;
  };

  const handleApiError = () => {
    if (apiErrorCallback.current) apiErrorCallback.current();
  };

  const handleSubscribe: Subscribe = (subscription) => {
    if (!socket.current) return console.warn(ACTIVE_WARNING);
    socket.current.subscribe(subscription);
  };

  const handleUnsubscribe: Unsubscribe = (endpoint) => {
    if (!socket.current) return console.warn(ACTIVE_WARNING);
    socket.current.unsubscribe(endpoint);
  };

  const handleJoin: Join = (joinSettings) => {
    if (!socket.current) return console.warn(ACTIVE_WARNING);
    socket.current.join(joinSettings);
  };

  const handleLeave: Leave = (endpoint) => {
    if (!socket.current) return console.warn(ACTIVE_WARNING);
    socket.current.leave(endpoint);
  };

  const handleSend: Send = (endpoint, data) => {
    if (!socket.current) return console.warn(ACTIVE_WARNING);
    socket.current.send(endpoint, data);
  };

  const handleGetSocket = () => {
    return socket.current;
  };

  const contextValue = {
    setApiErrorCallback: handleSetApiErrorCallback,
    subscribe: handleSubscribe,
    unsubscribe: handleUnsubscribe,
    join: handleJoin,
    leave: handleLeave,
    send: handleSend,
    getSocket: handleGetSocket,
  };

  return (
    <SocketsContext.Provider value={contextValue}>
      {children}
    </SocketsContext.Provider>
  );
}

export function useSockets() {
  const context = useContext(SocketsContext);
  if (!context) {
    throw Error('useSockets must be used within the SocketsProvider');
  }
  return context;
}
